import React, { useEffect, useState } from 'react';
import { me, devices } from '../lib/api.js';

const UDID_HELP_LINK = 'https://get.udid.io';

export default function Devices() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [registering, setRegistering] = useState(false);

  // v160.3.3 manual paste state
  const [mode, setMode] = useState('auto');           // 'auto' | 'manual'
  const [manualUdid, setManualUdid] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualClass, setManualClass] = useState('iphone');
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualSuccess, setManualSuccess] = useState('');

  useEffect(() => { refresh(); }, []);

  function refresh() {
    me.fetch().then(r => setData(r)).catch(e => setError(e.message));
  }

  async function startRegister() {
    setRegistering(true);
    setError('');
    try {
      const r = await devices.startRegistration();
      window.location.href = r.profileURL;
    } catch (e) {
      setError(e.message);
      setRegistering(false);
    }
  }

  function isValidUDIDClient(s) {
    const t = (s || '').trim();
    return /^[0-9a-fA-F]{40}$/.test(t) || /^[0-9a-fA-F]{8}-[0-9a-fA-F]{16}$/.test(t);
  }

  async function submitManual(e) {
    e.preventDefault();
    setError('');
    setManualSuccess('');
    if (!isValidUDIDClient(manualUdid)) {
      setError('Invalid UDID format. Expected 40 hex characters or the newer 25-character format with a hyphen (e.g., 00008101-001A2B3C4D5E6F70).');
      return;
    }
    setManualSubmitting(true);
    try {
      const r = await devices.registerManual(manualUdid.trim(), manualName.trim() || undefined, manualClass);
      setManualSuccess(r.message || 'Device registered. Pending Mac-side sync.');
      setManualUdid(''); setManualName('');
      refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setManualSubmitting(false);
    }
  }

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>My devices</h2>

      {error && <div className="error-banner">{error}</div>}
      {manualSuccess && (
        <div className="card" style={{borderLeft:'3px solid var(--success, #10b981)', background:'rgba(16,185,129,0.08)'}}>
          ✅ {manualSuccess}
        </div>
      )}
      {data === null && <div className="spinner" />}

      {data && (
        <>
          <div className="card">
            <h3>Register a device</h3>

            {/* Mode toggle */}
            <div style={{display:'flex', gap:8, margin:'12px 0'}}>
              <button
                onClick={() => { setMode('auto'); setError(''); setManualSuccess(''); }}
                style={{
                  flex:1, padding:'8px 12px', borderRadius:8, fontSize:13,
                  background: mode === 'auto' ? 'var(--accent)' : 'var(--bg-elev-2)',
                  color: mode === 'auto' ? 'white' : 'var(--text-muted)'
                }}>
                📲 Auto (this iPhone)
              </button>
              <button
                onClick={() => { setMode('manual'); setError(''); setManualSuccess(''); }}
                style={{
                  flex:1, padding:'8px 12px', borderRadius:8, fontSize:13,
                  background: mode === 'manual' ? 'var(--accent)' : 'var(--bg-elev-2)',
                  color: mode === 'manual' ? 'white' : 'var(--text-muted)'
                }}>
                ⌨️ Paste UDID manually
              </button>
            </div>

            {mode === 'auto' && (
              <>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 12px' }}>
                  Tap below from the iPhone or iPad you want to register. iOS will prompt to install a one-time profile that captures the UDID automatically.
                </p>
                <button className="btn btn-block" onClick={startRegister} disabled={registering}>
                  {registering ? 'Opening…' : 'Register this device'}
                </button>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8, textAlign: 'center' }}>
                  Remove the profile anytime in Settings → General → VPN &amp; Device Management.
                </p>
              </>
            )}

            {mode === 'manual' && (
              <form onSubmit={submitManual}>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 12px' }}>
                  Enter your device's UDID directly. Useful if you're registering from a different device, or if your iPhone is corporate-managed and blocks profile installs.
                </p>

                <label style={{display:'block', fontSize:13, fontWeight:600, marginBottom:4}}>
                  UDID <span style={{color:'var(--text-muted)', fontWeight:400}}>(40 hex chars, or 25 with hyphen)</span>
                </label>
                <input
                  type="text"
                  value={manualUdid}
                  onChange={e => setManualUdid(e.target.value)}
                  placeholder="00008101-001A2B3C4D5E6F70"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{
                    width:'100%', padding:'10px 12px', fontSize:13,
                    fontFamily:'SF Mono, Menlo, monospace',
                    background:'var(--bg-elev-2)', border:'1px solid var(--border)',
                    borderRadius:8, color:'var(--text)', marginBottom:10
                  }}
                />

                <label style={{display:'block', fontSize:13, fontWeight:600, marginBottom:4}}>
                  Device name <span style={{color:'var(--text-muted)', fontWeight:400}}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={manualName}
                  onChange={e => setManualName(e.target.value)}
                  placeholder="My iPhone 15 Pro"
                  maxLength={80}
                  style={{
                    width:'100%', padding:'10px 12px', fontSize:13,
                    background:'var(--bg-elev-2)', border:'1px solid var(--border)',
                    borderRadius:8, color:'var(--text)', marginBottom:10
                  }}
                />

                <label style={{display:'block', fontSize:13, fontWeight:600, marginBottom:4}}>
                  Device type
                </label>
                <select
                  value={manualClass}
                  onChange={e => setManualClass(e.target.value)}
                  style={{
                    width:'100%', padding:'10px 12px', fontSize:13,
                    background:'var(--bg-elev-2)', border:'1px solid var(--border)',
                    borderRadius:8, color:'var(--text)', marginBottom:14
                  }}>
                  <option value="iphone">iPhone</option>
                  <option value="ipad">iPad</option>
                  <option value="ipod">iPod touch</option>
                  <option value="mac">Mac (Apple Silicon)</option>
                  <option value="appletv">Apple TV</option>
                  <option value="watch">Apple Watch</option>
                </select>

                <button
                  type="submit"
                  className="btn btn-block"
                  disabled={manualSubmitting || !manualUdid.trim()}>
                  {manualSubmitting ? 'Registering…' : 'Register device'}
                </button>

                <details style={{marginTop:14, fontSize:12, color:'var(--text-muted)'}}>
                  <summary style={{cursor:'pointer', userSelect:'none'}}>How do I find my UDID?</summary>
                  <ul style={{paddingLeft:18, marginTop:8, lineHeight:1.6}}>
                    <li><strong>From this iPhone (no computer):</strong> visit <a href={UDID_HELP_LINK} target="_blank" rel="noreferrer" style={{color:'var(--accent)'}}>get.udid.io</a> in Safari and follow the prompts. Copy the displayed UDID and paste it above.</li>
                    <li><strong>Mac (macOS Catalina+):</strong> connect via USB, open <em>Finder</em>, click your device in the sidebar, click the device info text under the device name to cycle to UDID, right-click → Copy.</li>
                    <li><strong>Mac (older / iTunes):</strong> open iTunes, click the device icon, click <em>Serial Number</em> on the summary page to reveal UDID, right-click → Copy.</li>
                    <li><strong>Windows:</strong> install iTunes, connect via USB, click the device icon, click <em>Serial Number</em> on summary page to cycle to UDID, right-click → Copy. Or use 3uTools (free) which shows UDID directly.</li>
                  </ul>
                </details>
              </form>
            )}
          </div>

          <div className="card">
            <h3>Registered devices</h3>
            {data.devices.length === 0 && <div className="empty">No devices registered yet.</div>}
            <div className="list">
              {data.devices.map(d => (
                <div key={d.id} className="list-row">
                  <div className="icon">
                    {d.device_class === 'iphone' ? '📱'
                      : d.device_class === 'ipad' ? '📊'
                      : d.device_class === 'mac' ? '💻'
                      : d.device_class === 'appletv' ? '📺'
                      : d.device_class === 'watch' ? '⌚' : '📱'}
                  </div>
                  <div className="body">
                    <div className="title">{d.device_name || d.device_class}</div>
                    <div className="sub">
                      {d.product_type || d.device_class} · iOS {d.ios_version || '?'} · UDID {d.udid.slice(0, 8)}…
                      {!d.asc_synced_at && <span style={{marginLeft:8, color:'#f59e0b'}}>⏳ pending Apple sync</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
